import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type AppRole = Database['public']['Enums']['app_role'];

interface FeatureFlag {
  id: string;
  feature_key: string;
  feature_name: string;
  description: string | null;
  is_enabled: boolean;
  enabled_for_roles: AppRole[];
  category: string;
  sort_order: number;
  updated_at: string;
  updated_by: string | null;
}

interface FeatureFlagsContextType {
  flags: FeatureFlag[];
  userRoles: AppRole[];
  isLoading: boolean;
  isFeatureEnabled: (featureKey: string) => boolean;
  getCategoryState: (category: string) => 'all' | 'none' | 'mixed';
  refetch: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

export const FeatureFlagsProvider = ({ children }: { children: ReactNode }) => {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [userRoles, setUserRoles] = useState<AppRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchFlags = async () => {
    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('*')
        .order('category')
        .order('sort_order');

      if (error) throw error;
      setFlags(data || []);
    } catch (error) {
      console.error('Error fetching feature flags:', error);
    }
  };

  const fetchUserRoles = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setUserRoles([]);
        return;
      }

      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (error) throw error;
      setUserRoles(roles?.map(r => r.role) || []);
    } catch (error) {
      console.error('Error fetching user roles:', error);
      setUserRoles([]);
    }
  };

  const refetch = async () => {
    await Promise.all([fetchFlags(), fetchUserRoles()]);
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await refetch();
      setIsLoading(false);
    };
    init();

    // Subscribe to realtime changes
    const channel = supabase
      .channel('feature_flags_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'feature_flags',
        },
        () => {
          fetchFlags();
        }
      )
      .subscribe();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      fetchUserRoles();
    });

    return () => {
      channel.unsubscribe();
      subscription.unsubscribe();
    };
  }, []);

  const isFeatureEnabled = (featureKey: string): boolean => {
    // Admins bypass all feature flag restrictions
    if (userRoles.includes('Admin')) {
      return true;
    }

    const flag = flags.find(f => f.feature_key === featureKey);
    if (!flag) return false;

    // Check if globally disabled
    if (!flag.is_enabled) return false;

    // Check if user's role is in enabled_for_roles
    const enabledRoles = flag.enabled_for_roles ?? [];
    const userHasAccess = userRoles.some(role => 
      enabledRoles.includes(role)
    );

    return userHasAccess;
  };

  const getCategoryState = (category: string): 'all' | 'none' | 'mixed' => {
    const categoryFlags = flags.filter(f => f.category === category);
    if (categoryFlags.length === 0) return 'none';

    const enabledCount = categoryFlags.filter(f => f.is_enabled).length;
    if (enabledCount === 0) return 'none';
    if (enabledCount === categoryFlags.length) return 'all';
    return 'mixed';
  };

  return (
    <FeatureFlagsContext.Provider value={{
      flags,
      userRoles,
      isLoading,
      isFeatureEnabled,
      getCategoryState,
      refetch,
    }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};
